import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { KeyRound, Trash2 } from 'lucide-react';
import mcpServerService from '../../../services/mcpServerService';
import ApiKeyRevealDialog from '../../shared/ApiKeyRevealDialog';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from '../McpServerDetail.module.scss';
import {
  CREDENTIAL_MODES,
  CREDENTIAL_MODE_OPTIONS,
  LONG_RUNNING_COPY,
  OAUTH_CREDENTIAL_MODE,
  OAUTH_INBOUND_MODE,
  PER_USER_AUTH_DISABLED_HINT,
  PER_USER_AUTH_TYPES,
  UPSTREAM_AUTH_TYPES,
  allowsUrlKey,
  buildLongRunningBody,
  buildOauthConfigBody,
  describeError,
  formatLogTime,
  getCredentialMode,
  getSecretLabel,
  isAuthTypeAllowedPerUser,
  isOauthMode,
  isPublished,
  longRunningChanged,
  longRunningToForm,
  oauthConfigToForm,
  oauthFormChanged,
  secretChanged,
  storesNoUpstreamKey,
  supportsLongRunning,
  toBaseCredentialMode,
  validateLongRunningForm,
  validateOauthForm,
  withUrlKey,
} from '../mcpUtils';
import McpOAuthFields from '../McpOAuthFields';
import McpLongRunningFields from '../McpLongRunningFields';
import McpExposeErrorsField from '../McpExposeErrorsField';
import McpInstructionsField from '../McpInstructionsField';

/**
 * Settings and management (§10).
 *
 * The one thing that has to be right here: secrets come back masked
 * (`****1234`), and sending the mask back would overwrite the real credential
 * with literal asterisks. `secretChanged` gates that.
 */
const SettingsTab = ({ server, tokens, onMutated, onUnpublish, unpublishing, notify }) => {
  const navigate = useNavigate();

  const initialSecret = server.upstream_api_key || '';

  const [form, setForm] = useState({
    name: server.name || '',
    description: server.description || '',
    rate_limit_per_min: server.rate_limit_per_min ?? 60,
    upstream_base_url: server.upstream_base_url || '',
    upstream_auth_type: server.upstream_auth_type || 'BEARER_TOKEN',
    upstream_api_key: initialSecret,
    inbound_auth_mode: server.inbound_auth_mode || 'token',
    credential_mode: getCredentialMode(server),
    oauth: oauthConfigToForm(server.oauth_config),
    long_running: longRunningToForm(server),
    expose_upstream_errors: !!server.expose_upstream_errors,
    instructions: server.instructions || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [oauthErrors, setOauthErrors] = useState({});
  const [longRunningErrors, setLongRunningErrors] = useState({});
  // Staged rather than applied immediately: an instant PATCH would reload the
  // server and reset this form, silently discarding any other unsaved edits.
  const [clearKey, setClearKey] = useState(false);
  const [minting, setMinting] = useState(false);
  const [mintedToken, setMintedToken] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setForm({
      name: server.name || '',
      description: server.description || '',
      rate_limit_per_min: server.rate_limit_per_min ?? 60,
      upstream_base_url: server.upstream_base_url || '',
      upstream_auth_type: server.upstream_auth_type || 'BEARER_TOKEN',
      upstream_api_key: server.upstream_api_key || '',
      inbound_auth_mode: server.inbound_auth_mode || 'token',
      credential_mode: getCredentialMode(server),
      oauth: oauthConfigToForm(server.oauth_config),
      long_running: longRunningToForm(server),
      expose_upstream_errors: !!server.expose_upstream_errors,
      instructions: server.instructions || '',
    });
    setClearKey(false);
    setOauthErrors({});
    setLongRunningErrors({});
  }, [server]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const setLongRunningField = (key, value) => {
    setForm((f) => ({ ...f, long_running: { ...f.long_running, [key]: value } }));
    // Flipping the toggle hides or re-shows every field, so old errors go too.
    setLongRunningErrors((e) => (key === 'enabled' ? {} : e[key] ? { ...e, [key]: undefined } : e));
  };

  const isApi = server.server_type === 'api';
  // The API rejects the three fields outright on API and knowledge servers.
  const longRunning = supportsLongRunning(server);
  const published = isPublished(server);
  const anonymous = form.inbound_auth_mode === 'none';
  const baseMode = toBaseCredentialMode(form.credential_mode);
  const perUser = baseMode === 'per_user';
  const oauth = isOauthMode(baseMode);
  // Read off the inbound field rather than the credential mode, because this is
  // the field the access section edits — and the dropdown there has no `oauth`
  // option, so rendering it while the value is `oauth` would show a blank one.
  const oauthInbound = form.inbound_auth_mode === OAUTH_INBOUND_MODE;
  // Per-user and sign-in both leave the owner's key out of it entirely.
  const noStoredKey = storesNoUpstreamKey(form.credential_mode);
  const urlKey = allowsUrlKey(form.credential_mode);
  // Moving back to a shared key: nothing is stored in any other mode, so one
  // has to be supplied now.
  const switchingToShared =
    isApi &&
    !noStoredKey &&
    storesNoUpstreamKey(getCredentialMode(server)) &&
    form.upstream_auth_type !== 'NO_AUTH' &&
    !initialSecret;

  /**
   * Same rules as create: no stored key outside shared mode, no multi-secret
   * auth type, and OAuth carries `inbound_auth_mode` with it — the API refuses
   * to publish a server where the two disagree.
   */
  const handleCredentialModeChange = (mode) => {
    setForm((f) => {
      const next = { ...f };
      if (isOauthMode(mode)) {
        next.credential_mode = OAUTH_CREDENTIAL_MODE;
        next.inbound_auth_mode = OAUTH_INBOUND_MODE;
      } else {
        // The dropdown only picks the base mode; the checkbox opts into the URL
        // variant. Selecting per-user always starts on the header variant.
        next.credential_mode = mode === 'per_user' ? withUrlKey(false) : 'shared';
        if (f.inbound_auth_mode === OAUTH_INBOUND_MODE) next.inbound_auth_mode = 'token';
      }
      if (mode !== 'shared') next.upstream_api_key = '';
      // Single-secret is a per-user-key restriction. OAuth forwards the verified
      // token as `Authorization: Bearer` whatever this holds, so it is ignored
      // rather than constrained — and its dropdown is hidden.
      if (mode === 'per_user' && !isAuthTypeAllowedPerUser(f.upstream_auth_type)) {
        next.upstream_auth_type = PER_USER_AUTH_TYPES[1]; // BEARER_TOKEN
      }
      return next;
    });
    setOauthErrors({});
  };

  const handleUrlKeyChange = (allow) =>
    setForm((f) => ({ ...f, credential_mode: withUrlKey(allow) }));

  const setOauthField = (key, value) => {
    setForm((f) => ({ ...f, oauth: { ...f.oauth, [key]: value } }));
    setOauthErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const dirty = useMemo(
    () =>
      form.name !== (server.name || '') ||
      form.description !== (server.description || '') ||
      form.instructions !== (server.instructions || '') ||
      String(form.rate_limit_per_min) !== String(server.rate_limit_per_min ?? 60) ||
      form.upstream_base_url !== (server.upstream_base_url || '') ||
      form.upstream_auth_type !== (server.upstream_auth_type || 'BEARER_TOKEN') ||
      form.inbound_auth_mode !== (server.inbound_auth_mode || 'token') ||
      form.credential_mode !== getCredentialMode(server) ||
      form.expose_upstream_errors !== !!server.expose_upstream_errors ||
      (isOauthMode(toBaseCredentialMode(form.credential_mode)) &&
        oauthFormChanged(form.oauth, server.oauth_config)) ||
      (longRunning && longRunningChanged(form.long_running, server)) ||
      clearKey ||
      secretChanged(form.upstream_api_key, initialSecret),
    [form, server, initialSecret, clearKey, longRunning]
  );

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('The server needs a name.');
      return;
    }
    if (form.upstream_base_url && !/^https?:\/\//i.test(form.upstream_base_url.trim())) {
      setError('The base URL must start with http:// or https://');
      return;
    }
    // Coming back from per-user there is no stored key, so switching to shared
    // without typing one would save a server that can't authenticate at all.
    if (
      isApi &&
      !noStoredKey &&
      form.upstream_auth_type !== 'NO_AUTH' &&
      !initialSecret &&
      !form.upstream_api_key.trim()
    ) {
      setError(
        `A shared key is needed for ${form.upstream_auth_type}. Enter the ${getSecretLabel(
          form.upstream_auth_type
        )}, or set auth type to "No auth".`
      );
      return;
    }
    if (isApi && oauth) {
      const found = validateOauthForm(form.oauth);
      if (Object.keys(found).length) {
        setOauthErrors(found);
        setError('Check the highlighted sign-in settings.');
        return;
      }
      setOauthErrors({});
    }
    if (longRunning) {
      const found = validateLongRunningForm(form.long_running);
      if (Object.keys(found).length) {
        setLongRunningErrors(found);
        setError('Check the highlighted long-running request settings.');
        return;
      }
      setLongRunningErrors({});
    }

    const body = {
      name: form.name.trim(),
      description: form.description.trim(),
      rate_limit_per_min: Number(form.rate_limit_per_min),
      inbound_auth_mode: form.inbound_auth_mode,
    };
    // Sent only when changed. An empty string (not null, which reads as
    // "leave as is") clears it.
    if (form.instructions !== (server.instructions || '')) {
      body.instructions = form.instructions.trim();
    }
    if (isApi) {
      body.upstream_base_url = form.upstream_base_url.trim();
      body.upstream_auth_type = form.upstream_auth_type;
      body.credential_mode = form.credential_mode;
      body.expose_upstream_errors = form.expose_upstream_errors;
      if (clearKey) {
        // Explicit empty string, not null — null reads as "field omitted" in
        // most request models and would silently leave the key in place.
        body.upstream_api_key = '';
      } else if (!noStoredKey && secretChanged(form.upstream_api_key, initialSecret)) {
        // Only send the credential when it was actually retyped — the field is
        // pre-filled with a mask, and sending that back would destroy the key.
        // Per-user and sign-in servers never store one.
        body.upstream_api_key = form.upstream_api_key.trim();
      }
      if (oauth) body.oauth_config = buildOauthConfigBody(form.oauth, server.oauth_config);
    }

    // Switching deferral on adds `get_job_status` to the server, and apps only
    // see a new tool after reconnecting — so that toast says so.
    let savedMessage = 'Settings saved.';
    if (longRunning) {
      Object.assign(body, buildLongRunningBody(form.long_running, server));
      const wasOn = !!server.defer_after_seconds;
      if (!wasOn && form.long_running.enabled) savedMessage = LONG_RUNNING_COPY.savedOn;
      if (wasOn && !form.long_running.enabled) savedMessage = LONG_RUNNING_COPY.savedOff;
    }

    setSaving(true);
    setError(null);
    try {
      await mcpServerService.updateServer(server.id, body);
      await onMutated({ reloadServer: true, reloadTools: false });
      notify(savedMessage, 'success');
    } catch (e) {
      setError(describeError(e, "Couldn't save these settings."));
    } finally {
      setSaving(false);
    }
  };

  const handleMint = async () => {
    setMinting(true);
    try {
      const result = await mcpServerService.mintToken(server.id);
      setMintedToken(result?.token || null);
      await onMutated({ reloadServer: false, reloadTools: false, reloadTokens: true });
      if (!result?.token) notify('Token created, but the server did not return its value.', 'warning');
    } catch (e) {
      notify(describeError(e, "Couldn't create a token."), 'error');
    } finally {
      setMinting(false);
    }
  };

  const handleRevoke = async (token) => {
    setRevokeTarget(null);
    setRevokingId(token.id);
    try {
      await mcpServerService.revokeToken(server.id, token.id);
      await onMutated({ reloadServer: false, reloadTools: false, reloadTokens: true });
      notify('Token revoked.', 'info');
    } catch (e) {
      notify(describeError(e, "Couldn't revoke that token."), 'error');
    } finally {
      setRevokingId(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await mcpServerService.deleteServer(server.id);
      notify('Server deleted.', 'info');
      navigate('/mcp-servers');
    } catch (e) {
      notify(describeError(e, "Couldn't delete this server."), 'error');
      setDeleting(false);
    }
  };

  return (
    <>
      {/* ── Details ── */}
      <div className={styles.section}>
        <Typography className={styles.sectionTitle}>Details</Typography>
        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Description"
          value={form.description}
          onChange={(e) => setField('description', e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
        />
        <McpInstructionsField
          value={form.instructions}
          onChange={(v) => setField('instructions', v)}
          disabled={saving}
        />
        <TextField
          label="Rate limit (calls per minute)"
          value={form.rate_limit_per_min}
          onChange={(e) => setField('rate_limit_per_min', e.target.value)}
          type="number"
          size="small"
          sx={{ maxWidth: 260 }}
        />
      </div>

      {/* ── Long-running requests ── agent servers only; hidden entirely
          elsewhere, since the API refuses these fields there. */}
      {longRunning && (
        <div className={styles.section}>
          <Typography className={styles.sectionTitle}>{LONG_RUNNING_COPY.sectionTitle}</Typography>
          <McpLongRunningFields
            value={form.long_running}
            errors={longRunningErrors}
            onChange={setLongRunningField}
            disabled={saving}
          />
        </div>
      )}

      {/* ── Upstream ── */}
      {isApi && (
        <div className={styles.section}>
          <Typography className={styles.sectionTitle}>Upstream API</Typography>
          <span className={styles.sectionHint}>
            Where tool calls are sent, and the credential used to make them.
          </span>
          <TextField
            label="Base URL"
            value={form.upstream_base_url}
            onChange={(e) => setField('upstream_base_url', e.target.value)}
            size="small"
            fullWidth
            placeholder="https://api.acme.com/v1"
          />
          <FormControl size="small" sx={{ maxWidth: 340 }}>
            <InputLabel>Who provides the API key?</InputLabel>
            <Select
              label="Who provides the API key?"
              value={baseMode}
              onChange={(e) => handleCredentialModeChange(e.target.value)}
            >
              {CREDENTIAL_MODE_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <span className={styles.sectionHint}>{CREDENTIAL_MODES[baseMode].helper}</span>

          {perUser && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={urlKey}
                    onChange={(e) => handleUrlKeyChange(e.target.checked)}
                    size="small"
                  />
                }
                label="Allow the key in the URL"
              />
              <span className={styles.sectionHint}>
                For apps that can’t send custom headers — including Claude’s website connector
                screen. The key becomes part of the link, so treat it as replaceable.
              </span>

              {urlKey && form.inbound_auth_mode === 'token' && (
                <Alert severity="warning">
                  The access token can’t go in the URL, so Claude’s website still won’t be able to
                  connect. Set <strong>“Who can call this server”</strong> below to{' '}
                  <strong>“Anyone with the URL”</strong>.
                </Alert>
              )}
            </>
          )}

          {/* No explainer above the block — the credential-mode helper directly
              above already says nobody pastes a key, and the fields now load
              with the stored values, which say more than any banner could. */}
          {oauth && (
            <McpOAuthFields value={form.oauth} errors={oauthErrors} onChange={setOauthField} />
          )}

          {/* Hidden entirely in OAuth mode: the verified token is forwarded as
              `Authorization: Bearer` regardless of what this says, so offering
              the choice would be offering a control that does nothing. */}
          {!oauth && (
            <div className={styles.row}>
              <FormControl size="small" className={styles.grow}>
                <InputLabel>Auth type</InputLabel>
                <Select
                  label="Auth type"
                  value={form.upstream_auth_type}
                  onChange={(e) => setField('upstream_auth_type', e.target.value)}
                >
                  {UPSTREAM_AUTH_TYPES.map((a) => {
                    const blocked = perUser && !isAuthTypeAllowedPerUser(a.value);
                    return (
                      <MenuItem key={a.value} value={a.value} disabled={blocked}>
                        {a.label}
                        {blocked && (
                          <Tooltip title={PER_USER_AUTH_DISABLED_HINT}>
                            <span className={styles.menuHint}>not available</span>
                          </Tooltip>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {!noStoredKey && form.upstream_auth_type !== 'NO_AUTH' && (
                <TextField
                  label={getSecretLabel(form.upstream_auth_type)}
                  value={form.upstream_api_key}
                  onChange={(e) => setField('upstream_api_key', e.target.value)}
                  size="small"
                  className={styles.grow}
                  required={switchingToShared}
                  helperText={
                    switchingToShared
                      ? 'Required — this server has no stored key yet.'
                      : 'Leave the masked value alone to keep the current credential.'
                  }
                />
              )}
            </div>
          )}

          {/* Per-user servers store nothing, so moving to shared needs a key
              supplied here before the change can be saved. */}
          {switchingToShared && (
            <Alert severity="info">
              Switching to one shared key — enter the key everyone should act as. There isn’t one
              stored on this server yet.
            </Alert>
          )}

          {/* PER_USER_AUTH_DISABLED_HINT belongs on the disabled dropdown
              options, not here — as a banner it reads as though something on
              screen is unavailable. */}
          {perUser && (
            <Alert severity="info">
              No key is stored here — each person adds their own in their client, and only ever
              reaches their own data.
            </Alert>
          )}

          {/* Matches the lint warning the API raises for this exact state. The
              key field is hidden whenever nothing is stored, so this is the only
              way to act on it — without the button the advice is a dead end. */}
          {noStoredKey && !!initialSecret && !clearKey && (
            <Alert
              severity="warning"
              action={
                <Button size="small" className={styles.dangerBtn} onClick={() => setClearKey(true)}>
                  Clear it
                </Button>
              }
            >
              A shared key is still stored on this server but will never be used. Clear it to avoid
              confusion.
            </Alert>
          )}

          {clearKey && (
            <Alert
              severity="info"
              action={
                <Button size="small" onClick={() => setClearKey(false)}>
                  Undo
                </Button>
              }
            >
              The stored key will be removed when you save.
            </Alert>
          )}

          <McpExposeErrorsField
            checked={form.expose_upstream_errors}
            onChange={(v) => setField('expose_upstream_errors', v)}
            credentialMode={form.credential_mode}
          />
        </div>
      )}

      {/* ── Access ──
          OAuth replaces this setting outright, so in sign-in mode the whole
          section goes — except for tokens minted before the switch. Those are no
          longer accepted, and hiding them would leave them un-revokable. */}
      {(!oauthInbound || tokens.length > 0) && (
        <div className={styles.section}>
          {oauthInbound ? (
            <>
              <Typography className={styles.sectionTitle}>Leftover access tokens</Typography>
              <span className={styles.sectionHint}>
                These were minted before this server used sign-in. They are no longer accepted —
                revoke them so nobody keeps a copy.
              </span>
            </>
          ) : (
            <>
              <Typography className={styles.sectionTitle}>Who can call this server</Typography>
              <FormControl size="small" sx={{ maxWidth: 340 }}>
                <InputLabel>Inbound auth</InputLabel>
                <Select
                  label="Inbound auth"
                  value={form.inbound_auth_mode}
                  onChange={(e) => setField('inbound_auth_mode', e.target.value)}
                >
                  <MenuItem value="token">Requires a token (recommended)</MenuItem>
                  <MenuItem value="none">Anyone with the URL</MenuItem>
                </Select>
              </FormControl>

              {anonymous && (
                <Alert severity="warning">
                  The URL is the only thing protecting your upstream credentials, and it{' '}
                  <strong>will</strong> be crawled. Publishing is blocked outright while any write
                  tool is enabled on an anonymous server.
                </Alert>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  className={sharedStyles.outlineBtn}
                  variant="outlined"
                  size="small"
                  startIcon={minting ? <CircularProgress size={14} /> : <KeyRound size={15} />}
                  onClick={handleMint}
                  disabled={minting}
                >
                  {minting ? 'Creating…' : 'Create a token'}
                </Button>
                <span className={sharedStyles.muted}>
                  Token values are shown once at creation and cannot be retrieved.
                </span>
              </Box>
            </>
          )}

          {tokens.length > 0 && (
            <div className={sharedStyles.tableCard}>
              <div className={`${styles.tokenRow} ${styles.logHeadRow}`}>
                <span>Token</span>
                <span>Created</span>
                <span>Last used</span>
                <span>Expires</span>
                <span />
              </div>
              {tokens.map((token) => (
                <div
                  key={token.id}
                  className={`${styles.tokenRow} ${token.revoked_at ? styles.tokenRevoked : ''}`}
                >
                  <span className={styles.tokenLast4}>••••{token.last4}</span>
                  <span className={sharedStyles.muted}>{formatLogTime(token.created_at)}</span>
                  <span className={sharedStyles.muted}>
                    {token.last_used_at ? formatLogTime(token.last_used_at) : 'Never used'}
                  </span>
                  <span className={sharedStyles.muted}>
                    {token.revoked_at
                      ? `Revoked ${formatLogTime(token.revoked_at)}`
                      : token.expires_at
                      ? formatLogTime(token.expires_at)
                      : 'No expiry'}
                  </span>
                  <span>
                    {!token.revoked_at && (
                      <Button
                        className={styles.dangerBtn}
                        variant="outlined"
                        size="small"
                        onClick={() => setRevokeTarget(token)}
                        disabled={revokingId === token.id}
                      >
                        {revokingId === token.id ? 'Revoking…' : 'Revoke'}
                      </Button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button
          className={sharedStyles.primaryBtn}
          variant="contained"
          onClick={handleSave}
          disabled={saving || !dirty}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>

      {/* ── Danger zone ── */}
      <div className={`${styles.section} ${styles.dangerZone}`}>
        <Typography className={styles.sectionTitle}>Danger zone</Typography>

        {published && (
          <div className={styles.dangerRow}>
            <div>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Unpublish
              </Typography>
              <span className={styles.sectionHint}>
                The public URL starts returning 404 within seconds. Anything connected to it stops
                working.
              </span>
            </div>
            <Button
              className={styles.dangerBtn}
              variant="outlined"
              onClick={onUnpublish}
              disabled={unpublishing}
            >
              {unpublishing ? 'Unpublishing…' : 'Unpublish'}
            </Button>
          </div>
        )}

        <div className={styles.dangerRow}>
          <div>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Delete this server
            </Typography>
            <span className={styles.sectionHint}>
              Removes the server, its tools and its tokens. This cannot be undone.
            </span>
          </div>
          <Button
            className={styles.dangerBtn}
            variant="outlined"
            startIcon={<Trash2 size={15} />}
            onClick={() => {
              setDeleteConfirm('');
              setDeleteOpen(true);
            }}
          >
            Delete
          </Button>
        </div>
      </div>

      {/* Type-the-name confirm — deletion takes the tokens with it. */}
      <Dialog open={deleteOpen} onClose={deleting ? undefined : () => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete “{server.name}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            This deletes the server, all its tools, and every token issued for it. Anything
            connected to the public URL stops working immediately.
          </Typography>
          <TextField
            label={`Type "${server.name}" to confirm`}
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            className={styles.dangerBtn}
            variant="outlined"
            onClick={handleDelete}
            disabled={deleting || deleteConfirm !== server.name}
            startIcon={deleting ? <CircularProgress size={15} /> : null}
          >
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Revoke token ••••{revokeTarget?.last4}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Anything using this token stops working immediately. This cannot be undone — you would
            need to create a new token and update every client.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button
            className={styles.dangerBtn}
            variant="outlined"
            onClick={() => handleRevoke(revokeTarget)}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      <ApiKeyRevealDialog
        open={!!mintedToken}
        apiKey={mintedToken || ''}
        title="MCP access token created"
        subtitle="There is no way to retrieve it later. Copy it now, or you'll need to create another."
        requireExplicitDismiss
        onClose={() => setMintedToken(null)}
      />
    </>
  );
};

export default SettingsTab;
